import { SubscriptionStatus } from "../enums/subscription.enum";
import { AzConnectionSelectionEnum, ConnectionTypeEnum } from "../enums/vm.enum";
import { VMState } from "../enums/VMState";
import { AzConnectionSelection, ConnectionType } from "../types/vm.type";
import { VMStateLabels } from "./constants";

export class StringUtilities {

  static capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static isPascalCase(key: string): boolean {
    return /^[A-Z][a-zA-Z]*$/.test(key);
  }

  static isKebabCase(key: string): boolean {
    return /^[a-z]+(-[a-z]+)+$/.test(key);
  }

  static isSnakeCase(key: string): boolean {
    return /^[a-z]+(_[a-z]+)+$/.test(key);
  }

  static splitPascalCase(key: string): string[] {
    return key.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  }

  static formatKey(key: string): string {

    if (this.isPascalCase(key)) {
      return this.splitPascalCase(key).join(' ');
    }

    if (this.isKebabCase(key) || this.isSnakeCase(key)) {
      return key.replace(/[-_]/g, ' ');
    }
    return key.replace(/[-_]/g, ' ');
  }

  static cleanIPs(ipList: string): string {
    if (!ipList || typeof ipList !== 'string') return '';
    return ipList.split(',')
      .map(ip => ip.trim())
      .filter(ip => ip)
      .join(',');
  }

  static getVmStatusLabel(state: VMState): string {
    switch (state) {
      case VMState.Starting: return VMStateLabels.Starting;
      case VMState.Running: return VMStateLabels.Running;
      case VMState.Stopping: return VMStateLabels.Stopping;
      case VMState.Stopped: return VMStateLabels.Stopped;
      case VMState.Deallocating: return VMStateLabels.Deallocating;
      case VMState.Deallocated: return VMStateLabels.Deallocated;
      case VMState.Unknown: return VMStateLabels.Unknown;
      default: return VMStateLabels.Unknown;
    }
  }

  static formatCamelCase(variableName: string): string {
    const formattedName = variableName.replace(/([a-z])([A-Z])/g, '$1 $2');
    return this.capitalizeFirstLetter(formattedName);
  }

  static getVendorName(vendor: string){
    switch(vendor?.toLowerCase()) {
      case "aws": return vendor.toUpperCase();
      case "azure": return this.capitalizeFirstLetter(vendor)
    }
  }

  static replaceAllOccurrences(source: string, searchValue: string, replaceValue: string): string {
    return source.split(searchValue).join(replaceValue);
  }

  static truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
      return str;
    }
    return str.slice(0, maxLength) + '...';
  }

  static getSubscriptionStatusClass(status: string): string {
    
    const classMapping: Record<string, string> = {
      [SubscriptionStatus.AwaitingActivation]: "awaiting-activation",
      [SubscriptionStatus.AwaitingPlanSelection]: "awaiting-plan-selection",
      [SubscriptionStatus.AwaitingQuotation]: "awaiting-quotation",
      [SubscriptionStatus.Active]: "active",
      [SubscriptionStatus.Expired]: "expired",
      [SubscriptionStatus.Unsubscribed]: "unsubscribed",
      [SubscriptionStatus.UnsubscribePending]: "unsubscribe-pending",
      [SubscriptionStatus.Suspended]: "suspended",
    };
  
    return classMapping[status] || "unknown-status";
  }

  static getValidConnectionType(connectionType: ConnectionType): ConnectionType {
    const allowedConnectionTypes = Object.values(ConnectionTypeEnum)
    return allowedConnectionTypes.includes(connectionType) ? connectionType : ConnectionTypeEnum.PRIVATE_LINK
  }

  static getValidAzConnectionSelection(connectionType: AzConnectionSelection): AzConnectionSelection {
    const allowedConnectionTypes = Object.values(AzConnectionSelectionEnum)
    return allowedConnectionTypes.includes(connectionType) ? connectionType : AzConnectionSelectionEnum.PUBLIC_IPS;
  };

}
